import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { loadBusinesses } from '../../actions/business';
import BusinessCard from './BusinessCard';
import Filters from './Filters';
import IslandRail from '../island/IslandRail';
import Loader from '../common/Loader';

export default function Directory() {
  const dispatch = useDispatch();
  const { categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const { items, meta } = useSelector((state) => state.businesses);
  const towns = useSelector((state) => state.config.towns);
  const loading = useSelector((state) => Boolean(state.api.loading.BUSINESSES));

  // /directory/eat-drink and /directory?category_slug=eat-drink are the same
  // view. The path form is the one that gets linked and indexed; the query form
  // is what the filter control writes. The path wins so a pretty URL cannot be
  // silently overridden by a stale query param.
  const params = useMemo(() => {
    const fromQuery = Object.fromEntries(searchParams.entries());

    return categorySlug ? { ...fromQuery, category_slug: categorySlug } : fromQuery;
  }, [searchParams, categorySlug]);

  const page = Number(params.page || 1);

  useEffect(() => {
    const { page: _ignored, ...filters } = params;

    dispatch(loadBusinesses(filters, page));
  }, [dispatch, params, page]);

  const onChange = (next) => {
    const clean = Object.fromEntries(
      Object.entries({ ...next, page: undefined }).filter(([, v]) => v !== undefined && v !== '')
    );

    setSearchParams(clean);
  };

  const goToPage = (n) => setSearchParams({ ...params, page: String(n) });

  return (
    <div className="wrap pad directory">
      <header className="directory-head">
        <h1>Directory</h1>
        {meta && (
          <p className="muted">
            {meta.total} {meta.total === 1 ? 'listing' : 'listings'}
          </p>
        )}
      </header>

      <div className="directory-body">
        {/* The rail sits above the filter panel rather than inside it: it is the
            primary way people move around an island eighteen miles long, and
            burying it under a Town select would make it the third control on
            the page instead of the first. */}
        <div className="directory-side">
          <IslandRail towns={towns} />
          <Filters params={params} onChange={onChange} onReset={() => setSearchParams({})} />
        </div>

        <section className="results">
          {loading && <Loader label="Loading listings" />}

          {!loading && items.length === 0 && (
            <div className="empty">
              <h2>Nothing matches that yet</h2>
              <p>
                The directory is new. Clear a filter, or add the business you were looking for.
              </p>
            </div>
          )}

          <div className="card-grid">
            {items.map((business) => (
              <BusinessCard key={business.id} business={business} />
            ))}
          </div>

          {meta && meta.last_page > 1 && (
            <nav className="pager" aria-label="Pagination">
              <button
                type="button"
                className="btn"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
              >
                Previous
              </button>
              <span className="muted">
                Page {meta.current_page} of {meta.last_page}
              </span>
              <button
                type="button"
                className="btn"
                disabled={page >= meta.last_page}
                onClick={() => goToPage(page + 1)}
              >
                Next
              </button>
            </nav>
          )}
        </section>
      </div>
    </div>
  );
}
